import { QueryClientContext } from '@tanstack/react-query';
import { useContext } from 'react';

import { useProductParts } from '@/features/storefront/parts/context';
import { ProductAddToCartPart, type ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { WishlistButton } from '@/features/storefront/wishlist/WishlistButton';
import { useWishlistPlugin } from '@/features/storefront/wishlist/useWishlist';

/**
 * The product page's add-to-cart button, with the wishlist heart beside it
 * when the plugin asks for one there (`show_on_product`).
 *
 * The heart follows the button rather than being a component of its own a
 * merchant places: it belongs next to the buying decision, and the plugin's
 * switch is the one place it is turned on and off. Without the plugin - or
 * without the providers it reads, as in a test drawing one part - this is
 * exactly the button it always was.
 */
export function ProductAddToCartWithWishlistPart(props: ProductPartProps) {
  const client = useContext(QueryClientContext);
  if (!client) return <ProductAddToCartPart {...props} />;
  return <WithHeart {...props} />;
}

function WithHeart(props: ProductPartProps) {
  const product = useProductParts();
  const plugin = useWishlistPlugin();
  if (!product || !plugin.on || !plugin.show_on_product) return <ProductAddToCartPart {...props} />;
  return (
    <div className="flex items-end gap-3" data-product-wishlist>
      <ProductAddToCartPart {...props} />
      <WishlistButton productId={product.detail.id} variant="product" className="h-12 w-12" />
    </div>
  );
}
